import { useCallback, useMemo } from 'react';
import { useSurveyFilters } from '@hotjar/feedback/hooks/useSurveyFilters';
import { filtersToSearchClauses } from '@hotjar/feedback';
import { FileTooLargeModal } from '@hotjar/feedback/components/common/FileTooLargeModal/FileTooLargeModal';
import { useDownloadActions } from './useDownloadActions';

type ExportResponses = {
  beginExport: (exportType: 'csv' | 'xlsx', sortBy?: string) => void;
  fileTooLargeModal: JSX.Element | null;
};

export const useExportResponses = (surveyId: number | undefined): ExportResponses => {
  const { localFilters, globalFilters } = useSurveyFilters();

  const { handleDownload, continueDownload, isFileTooLarge, cancelDownload } = useDownloadActions();

  const fileTooLargeModal = useMemo(
    () =>
      isFileTooLarge ? (
        <FileTooLargeModal isOpen onClose={cancelDownload} onContinue={continueDownload} />
      ) : null,
    [cancelDownload, continueDownload, isFileTooLarge],
  );

  const beginExport = useCallback(
    (exportType: 'csv' | 'xlsx', sortBy?: string) => {
      if (surveyId === undefined) throw new Error('Survey Id is undefined');
      handleDownload({
        surveyId,
        clauses: filtersToSearchClauses(localFilters, globalFilters),
        sortBy,
        format: exportType,
      });
    },
    [globalFilters, handleDownload, localFilters, surveyId],
  );

  return useMemo(() => ({ beginExport, fileTooLargeModal }), [beginExport, fileTooLargeModal]);
};
